/**
 * ChartsTest Page
 *
 * Test page for locally developing and testing the Chart components.
 * Displays all chart types with sample market analysis data.
 *
 * Access via: /guide/charts-test
 */

import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import {
  LineChart,
  PieChart,
  BarChart,
  ScatterChart,
  formatPercentage,
  formatMultiplier
} from '#client/components/Charts';

// ============================================================================
// SAMPLE DATA - Market Analysis Report
// ============================================================================

const marketAnalysisData = {
  marketGrowthTrend: {
    overview:
      '5-year historical and projected growth analysis for the global online home furnishings market.',
    trend: {
      series: [
        { year: 2021, revenue: 210000000000, index: 1000, isProjected: false },
        { year: 2022, revenue: 235000000000, index: 1119, isProjected: false },
        { year: 2023, revenue: 270000000000, index: 1286, isProjected: false },
        { year: 2024, revenue: 310000000000, index: 1476, isProjected: true },
        { year: 2025, revenue: 350000000000, index: 1667, isProjected: true },
        { year: 2026, revenue: 395000000000, index: 1881, isProjected: true }
      ],
      indexBaseYear: 2021,
      indexBaseValue: 1000
    },
    metrics: {
      cagr: 0.125,
      cagrDisplay: '12.5% (2021-2026)',
      latestRevenue: 310000000000,
      latestRevenueDisplay: '$310 Billion (2024)',
      annualGrowthRateDisplay: '12.5%'
    }
  },

  customerSegmentation: {
    overview:
      'Understanding distinct customer segments is vital for tailoring product offerings and marketing strategies.',
    segments: [
      {
        segmentName: 'Design-Conscious Millennials',
        percentage: 35,
        ageRange: '25-40',
        description:
          'Seek trendy, aesthetically pleasing, and unique home decor. Often influenced by social media.',
        keyNeeds: ['Unique designs', 'Quality materials', 'Social proof', 'Sustainability']
      },
      {
        segmentName: 'Value Seekers',
        percentage: 30,
        ageRange: '30-55',
        description:
          'Prioritize affordability, durability, and functionality. Look for sales and discounts.',
        keyNeeds: ['Affordable pricing', 'Durability', 'Functionality', 'Good customer service']
      },
      {
        segmentName: 'Convenience-Driven Professionals',
        percentage: 20,
        ageRange: '30-60',
        description:
          'Busy individuals who value ease of shopping and quick delivery. Price is secondary.',
        keyNeeds: ['Fast shipping', 'Easy browsing', 'Reliable quality', 'Excellent support']
      },
      {
        segmentName: 'Eco-Conscious Buyers',
        percentage: 15,
        ageRange: '25-65',
        description:
          'Seek products made from sustainable materials with ethical production practices.',
        keyNeeds: [
          'Sustainable materials',
          'Ethical sourcing',
          'Transparency',
          'Artisanal products'
        ]
      }
    ]
  },

  competitiveLandscape: {
    overview:
      'Understanding the competitive environment is critical for identifying differentiation opportunities.',
    competitors: [
      {
        name: 'Amazon',
        marketShare: 0.38,
        priceLevel: 'Budget',
        qualityRating: '3.8/5',
        customerSatisfaction: '4.0/5'
      },
      {
        name: 'Wayfair',
        marketShare: 0.15,
        priceLevel: 'Mid-Tier',
        qualityRating: '4.1/5',
        customerSatisfaction: '4.2/5'
      },
      {
        name: 'Target',
        marketShare: 0.05,
        priceLevel: 'Mid-Tier',
        qualityRating: '4.0/5',
        customerSatisfaction: '4.1/5'
      },
      {
        name: 'Overstock',
        marketShare: 0.03,
        priceLevel: 'Budget',
        qualityRating: '3.7/5',
        customerSatisfaction: '3.9/5'
      }
    ]
  },

  roiImpactLayer: {
    overview:
      'Expected return on investment (ROI) versus implementation effort for each strategic recommendation.',
    recommendationsROI: [
      {
        title: 'Dynamic Pricing & Value Optimization',
        expectedROI: 5,
        effortLevel: 4,
        roiCategory: 'Quick Win',
        roiNotes:
          'Relatively lower effort with direct impact on revenue. Requires data analysis and system integration.'
      },
      {
        title: 'Targeted Marketing & Community Building',
        expectedROI: 6.5,
        effortLevel: 6,
        roiCategory: 'Strategic Growth',
        roiNotes:
          'Improved customer acquisition efficiency and brand loyalty. Requires ongoing content creation.'
      },
      {
        title: 'Niche Product Curation & Brand Storytelling',
        expectedROI: 7,
        effortLevel: 7,
        roiCategory: 'Strategic Growth',
        roiNotes:
          'High potential for market differentiation and premium pricing. Requires significant product development.'
      },
      {
        title: 'Enhanced Digital Customer Experience',
        expectedROI: 8.5,
        effortLevel: 8,
        roiCategory: 'Long-Term Investment',
        roiNotes:
          'Significant impact on conversion rates and brand perception. Requires investment in technology.'
      }
    ],
    summaryMetrics: {
      averageROI: 6.75,
      averageEffort: 6.25,
      roiToEffortRatio: 1.08
    }
  }
};

// ============================================================================
// COLOR CONFIGURATIONS
// ============================================================================

const PRICE_TIER_COLORS = {
  Budget: '#6366F1',
  'Mid-Tier': '#06B6D4',
  Premium: '#059669'
};

const ROI_CATEGORY_COLORS = {
  'Quick Win': '#10B981',
  'Strategic Growth': '#2563EB',
  'Long-Term Investment': '#8B5CF6',
  'Low-Priority': '#94A3B8'
};

const SEGMENT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// ============================================================================
// COMPONENT
// ============================================================================

export default function ChartsTest() {
  // Transform data for LineChart
  // const lineChartData = marketAnalysisData.marketGrowthTrend.trend.series.map((item) => ({
  //   year: item.year,
  //   revenue: item.revenue,
  //   series: item.isProjected ? 'projected' : 'historical'
  // }));

  // Transform data for PieChart
  const pieChartData = marketAnalysisData.customerSegmentation.segments.map((seg, index) => ({
    name: seg.segmentName,
    value: seg.percentage,
    color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
    ageRange: seg.ageRange,
    keyNeeds: seg.keyNeeds
  }));

  // Transform data for BarChart
  const barChartData = marketAnalysisData.competitiveLandscape.competitors.map((comp) => ({
    name: comp.name,
    marketShare: comp.marketShare,
    priceLevel: comp.priceLevel,
    qualityRating: comp.qualityRating,
    customerSatisfaction: comp.customerSatisfaction
  }));

  // Transform data for ScatterChart
  const scatterChartData = marketAnalysisData.roiImpactLayer.recommendationsROI.map((rec) => ({
    x: rec.effortLevel,
    y: rec.expectedROI,
    title: rec.title,
    category: rec.roiCategory,
    notes: rec.roiNotes
  }));

  return (
    <Box
      sx={{
        padding: 4,
        backgroundColor: (theme) => theme.palette.background.default,
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Text size="xl" weight="bold">
          Charts Component Test Page
        </Text>
        <Text color="secondary" mt={1} size="sm">
          Testing all chart components with Market Analysis sample data
        </Text>
      </Box>

      {/* Charts Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: 4
        }}
      >
        {/* Line Chart - Single Series */}
        <Box>
          <Text mb={2} size="md" weight="bold">
            1b. Line Chart - Single Series (No Series Key)
          </Text>
          <LineChart
            color="#10B981"
            data={marketAnalysisData.marketGrowthTrend.trend.series.map((item) => ({
              year: item.year,
              index: item.index
            }))}
            formatY={(v) => v.toLocaleString()}
            height={300}
            showLegend={false}
            title="Market Index Growth (Base Year 2021 = 1000)"
            xKey="year"
            yAxisLabel="Index"
            yKey="index"
          />
        </Box>
        {/* Line Chart - Market Growth Trend */}
        {/* <Box>
          <Text mb={2} size="md" weight="bold">
            1. Line Chart - Market Growth Trend
          </Text>
          <LineChart
            data={lineChartData}
            formatTooltip={(point) => ({
              title: `Year ${point.year}`,
              Revenue: formatCurrency(point.revenue),
              badge: point.series === 'projected' ? 'Projected' : null
            })}
            formatY={formatCurrency}
            height={350}
            referenceLines={[{ x: 2024, label: 'Current' }]}
            seriesConfig={{
              historical: { color: '#2563EB', strokeStyle: 'solid', label: 'Historical' },
              projected: { color: '#93C5FD', strokeStyle: 'dashed', label: 'Projected' }
            }}
            seriesKey="series"
            title="5-Year Market Revenue Trend"
            xKey="year"
            yKey="revenue"
          />
        </Box> */}
        {/* Pie Chart - Customer Segmentation */}
        <Box>
          <Text mb={2} size="md" weight="bold">
            2. Pie Chart - Customer Segmentation
          </Text>
          <PieChart
            colorKey="color"
            data={pieChartData}
            formatTooltip={(item) => ({
              title: item.name,
              'Market Share': `${item.value}%`,
              'Age Range': item.ageRange,
              badge: item.keyNeeds?.[0]
            })}
            formatValue={(v) => `${v}%`}
            height={350}
            nameKey="name"
            title="Customer Segments by Market Share"
            valueKey="value"
            variant="pie"
          />
        </Box>
        {/* Bar Chart - Competitive Landscape (Horizontal) */}
        {/* <Box>
          <Text mb={2} size="md" weight="bold">
            3. Bar Chart - Competitive Landscape (Horizontal)
          </Text>
          <BarChart
            colorKey="priceLevel"
            colorMap={PRICE_TIER_COLORS}
            data={barChartData}
            formatTooltip={(item) => ({
              title: item.name,
              'Market Share': formatPercentage(item.marketShare),
              'Price Level': item.priceLevel,
              Quality: item.qualityRating,
              Satisfaction: item.customerSatisfaction
            })}
            formatY={(v) => formatPercentage(v)}
            height={300}
            layout="horizontal"
            title="Market Share by Competitor"
            xKey="name"
            yKey="marketShare"
          />
        </Box> */}
        {/* Bar Chart - Competitive Landscape (Vertical) */}
        <Box>
          <Text mb={2} size="md" weight="bold">
            3b. Bar Chart - Vertical Layout
          </Text>
          <BarChart
            barSize={150}
            colorKey="priceLevel"
            colorMap={PRICE_TIER_COLORS}
            data={barChartData}
            formatY={(v) => formatPercentage(v)}
            height={450}
            layout="vertical"
            title="Market Share Comparison"
            xKey="name"
            yKey="marketShare"
          />
        </Box>
        {/* Scatter Chart - ROI Impact Layer */}
        <Box sx={{ gridColumn: 'span 2' }}>
          <Text mb={2} size="md" weight="bold">
            4. Scatter Chart - ROI vs Effort Analysis
          </Text>
          <ScatterChart
            categoryColors={ROI_CATEGORY_COLORS}
            categoryKey="category"
            data={scatterChartData}
            formatTooltip={(item) => ({
              title: item.title,
              'Expected ROI': formatMultiplier(item.y),
              'Effort Level': `${item.x}/10`,
              badge: item.category
            })}
            formatX={(v) => `${v}`}
            formatY={(v) => `${v}x`}
            height={450}
            labelKey="title"
            pointSize={6}
            quadrantLines={{ x: 5, y: 6 }}
            quadrants={{
              topLeft: { label: 'Quick Win', color: '#10B98118' },
              topRight: { label: 'Strategic Growth', color: '#2563EB18' },
              bottomLeft: { label: 'Low Priority', color: '#94A3B818' },
              bottomRight: { label: 'Long-Term Investment', color: '#8B5CF618' }
            }}
            title="Strategic Recommendations: ROI vs Implementation Effort"
            xAxisLabel="Effort Level (1-10)"
            xDomain={[0, 10]}
            xKey="x"
            yAxisLabel="Expected ROI (multiplier)"
            yDomain={[0, 10]}
            yKey="y"
          />
        </Box>{' '}
        {/* Pie Chart - Pie Variant */}
        {/* <Box>
          <Text mb={2} size="md" weight="bold">
            2b. Pie Chart - Solid Pie Variant
          </Text>
          <PieChart
            colorKey="color"
            data={pieChartData}
            formatValue={(v) => `${v}%`}
            height={300}
            legendPosition="right"
            nameKey="name"
            title="Segments (Pie Variant)"
            valueKey="value"
            variant="pie"
          />
        </Box> */}
      </Box>

      {/* Empty State Examples */}
      {/* <Box sx={{ marginTop: 6 }}>
        <Text mb={3} size="lg" weight="bold">
          Empty State Examples
        </Text>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 3
          }}
        >
          <LineChart data={[]} title="Line Chart - Empty" />
          <PieChart data={[]} title="Pie Chart - Empty" />
          <BarChart data={[]} title="Bar Chart - Empty" />
          <ScatterChart data={[]} title="Scatter Chart - Empty" />
        </Box>
      </Box> */}
    </Box>
  );
}
